import React, { useMemo } from "react";
import { Box, GlobalStyles } from "@mui/material";
import { nFormatter, replaceSpecialCh } from "../../Helpers";
import {
  HeroFilterBar,
  MdKpiTile,
  SectionHeader,
  UnitSalesCard,
  CollectionsBoxesCard,
  TargetRingCard,
  MdRankListCard,
  FleetSizeCard,
  MonthlyNetChartCard,
  TopVehiclesCard,
  MdPeriodSelect,
  FiltersOption,
  FiltersOptionWithoutMTD,
  MD_GRID,
  mdGridSx,
  mdPageSx,
} from "./components";
import { MdCurrencyHint } from "./components/MdMoneyValue";
import { dashMainViewScrollbarStyles } from "../shared/dashboardEnterprise";

const tooltipGlobals = (
  <GlobalStyles
    styles={{
      ".highcharts-tooltip-container": {
        pointerEvents: "none !important",
        zIndex: "10050 !important",
      },
      ".highcharts-tooltip": { zIndex: "10050 !important" },
      ".MuiTooltip-popper": { zIndex: "10060 !important" },
      /* Keep a single page scroll (.main-view only) */
      "html, body": {
        overflow: "hidden",
        height: "100%",
      },
      ...dashMainViewScrollbarStyles,
      ".has-padding--single-scroll": {
        overflow: "visible !important",
        maxHeight: "none !important",
      },
    }}
  />
);

const MaintenanceDashboardView = ({
  translate,
  loginUserCurrency,
  // KPI / tab1
  kpiLoading,
  totalSales,
  totalAccounts,
  totalFleetSize,
  maintRequestTotal,
  avgTicket,
  overdue,
  monthlySale,
  monthlyTarget,
  tab1,
  onTabChange,
  // unit sales
  unitLoading,
  parts,
  services,
  packages,
  partsPct,
  servicesPct,
  packagesPct,
  tab2,
  // collections
  collectionsLoading,
  collectionsData,
  tab3,
  // targets
  allSaleData,
  monthlyTargetLoading,
  yearlyTargetLoading,
  // clients
  salesClientsLoading,
  salesClients,
  tab5,
  fleetLoading,
  fleetRows,
  openGraph,
  fleetChartOptions,
  onFleetRowClick,
  onFleetBack,
  overdueClientsLoading,
  overdueClients,
  tab7,
  jobCardsLoading,
  jobCards,
  tab8,
  // top sellers
  partsLoading,
  usedParts,
  tab9,
  servicesLoading,
  usedServices,
  tab10,
  packagesLoading,
  usedPackages,
  tab11,
  // fleet & profit
  vehicleCatsLoading,
  vehicleCategories,
  tab13,
  profitLoading,
  profitRows,
  tab14,
  stationsLoading,
  stations,
  tab15,
  // trend
  monthlyNetLoading,
  monthlySaleNetAmount,
  vehiclesLoading,
  topVehicles,
  topPerDay,
  topPerKm,
}) => {
  const ticketRaw =
    avgTicket != null && Number.isFinite(Number(avgTicket))
      ? Number(avgTicket)
      : 0;

  const targetPct =
    monthlyTarget > 0 ? Math.min((monthlySale / monthlyTarget) * 100, 100) : 0;
  const overduePct =
    totalSales > 0 ? Math.min((overdue / totalSales) * 100, 100) : 8;

  const salesRows = useMemo(
    () =>
      (salesClients?.clientList || []).map((d) => ({
        label: d.name,
        value: d.saleNetAmount,
        barPct: d.salesPercentage,
        raw: d,
      })),
    [salesClients],
  );

  const fleetRankRows = useMemo(
    () =>
      (fleetRows || []).map((d) => ({
        label: d.name || d.contractNumber || d.accountName || d.clientName,
        name: d.name || d.contractNumber || d.accountName || d.clientName,
        value: d.accessVehiclesCount ?? d.y ?? d.count,
        accessVehiclesCount: d.accessVehiclesCount ?? d.y ?? d.count,
        barPct: d.salesPercentage ?? d.countPercentage,
        salesPercentage: d.salesPercentage ?? d.countPercentage,
        raw: d,
      })),
    [fleetRows],
  );

  const overdueRows = useMemo(
    () =>
      (overdueClients?.clientList || []).map((d) => ({
        label: d.name,
        value: d.overDue,
        barPct: d.salesPercentage,
        raw: d,
      })),
    [overdueClients],
  );

  const jobRows = useMemo(
    () =>
      (jobCards?.clientList || []).map((d) => ({
        label: d.name,
        value: d.count,
        barPct: d.countPercentage,
        raw: d,
      })),
    [jobCards],
  );

  const partRows = useMemo(
    () =>
      (usedParts || []).map((d) => ({
        label: `${d.itemName || ""} - ${d.itemCode || ""}`.trim(),
        value: d.usedCount,
        barPct: d.usedCountPercentage,
        raw: d,
      })),
    [usedParts],
  );

  const serviceRows = useMemo(
    () =>
      (usedServices || []).map((d) => ({
        label: `${d.itemName || d.name || ""} - ${d.itemCode || ""}`.trim(),
        value: d.usedCount,
        barPct: d.usedCountPercentage,
        raw: d,
      })),
    [usedServices],
  );

  const packageRows = useMemo(
    () =>
      (usedPackages || []).map((d) => ({
        label: `${d.itemName || d.name || ""} - ${d.itemCode || ""}`.trim(),
        value: d.usedCount,
        barPct: d.usedCountPercentage,
        raw: d,
      })),
    [usedPackages],
  );

  const catRows = useMemo(
    () =>
      (vehicleCategories || []).map((d) => ({
        label: d.name === "car" ? "SEDAN" : String(d.name || "").toUpperCase(),
        value: d.value,
        barPct: d.countPercentage,
        raw: d,
      })),
    [vehicleCategories],
  );

  const profitList = useMemo(
    () =>
      (profitRows || []).map((d) => ({
        label: replaceSpecialCh(d.name),
        value: d.profit,
        barPct: d.countPercentage,
        raw: d,
      })),
    [profitRows],
  );

  const stationRows = useMemo(
    () =>
      (stations || []).map((d) => ({
        label: d.name,
        value: d.invoicesCount,
        barPct: d.countPercentage,
        raw: d,
      })),
    [stations],
  );

  return (
    <Box sx={mdPageSx}>
      {tooltipGlobals}

      <HeroFilterBar
        title={translate("fleetServiceDashboard") || "Fleet Service Dashboard"}
        subtitle={
          translate("fleetServiceSubtitle") ||
          "Maintenance · collections · targets · fleet"
        }
        controls={
          <MdPeriodSelect
            value={tab1}
            onChange={(opt) => onTabChange("tab1", opt)}
            options={FiltersOptionWithoutMTD}
            fallbackLabel="ALL"
          />
        }
      />

      <Box component="section" aria-label="Key figures" sx={mdGridSx(MD_GRID.kpis)}>
        <MdKpiTile
          accent="t1"
          title={translate("totalSales") || "Total Sales"}
          infoTitle={translate("mdTotalSalesInfo")}
          value={totalSales}
          rawValue={totalSales}
          money
          currency={loginUserCurrency}
          subtitle={translate("exclVat")}
          footer={translate("invoiced") || "Invoiced"}
          meter={100}
          loading={kpiLoading}
        />
        <MdKpiTile
          accent="t2"
          title={translate("totalAccounts") || "Total Accounts"}
          infoTitle={translate("mdTotalAccountsInfo")}
          value={totalAccounts}
          rawValue={totalAccounts}
          valueSuffix={translate("accounts") || "Accounts"}
          subtitle={translate("activeFleetClients")}
          footer={`${translate("fleetSizeLabel")} ${
            totalFleetSize ? nFormatter(totalFleetSize, 2) : 0
          }`}
          meter={82}
          loading={kpiLoading}
        />
        <MdKpiTile
          accent="t3"
          title={translate("maintenanceRequests") || "Maintenance Requests"}
          infoTitle={translate("mdMaintenanceRequestsInfo")}
          value={maintRequestTotal}
          rawValue={maintRequestTotal}
          subtitle={translate("partsServicesPackages")}
          footer={translate("unitSales")}
          meter={70}
          loading={unitLoading}
        />
        <MdKpiTile
          accent="t4"
          title={translate("averageTicketPrice") || "Avg. Ticket Price"}
          infoTitle={translate("mdAvgTicketInfo")}
          value={ticketRaw}
          rawValue={ticketRaw}
          money
          currency={loginUserCurrency}
          subtitle={translate("perJobCard")}
          footer={translate("avgPerInvoice")}
          meter={48}
          loading={kpiLoading}
        />
        <MdKpiTile
          accent="t5"
          title={translate("overdue") || "Overdue"}
          infoTitle={translate("mdOverdueInfo")}
          value={overdue}
          rawValue={overdue}
          money
          currency={loginUserCurrency}
          subtitle={translate("requiresFollowUp")}
          footer={
            totalSales
              ? `${((overdue / totalSales) * 100 || 0).toFixed(1)}% ${translate(
                  "ofSales",
                )}`
              : translate("pastDue")
          }
          meter={overduePct || 10}
          loading={kpiLoading}
        />
        <MdKpiTile
          accent="t6"
          title={translate("monthlyTarget") || "Monthly Target"}
          infoTitle={translate("mdMonthlyTargetInfo")}
          value={monthlyTarget}
          rawValue={monthlyTarget}
          money
          currency={loginUserCurrency}
          subtitle={`${targetPct.toFixed(0)}${translate("pctReached")} · ${translate(
            "sale",
          )} ${monthlySale ? nFormatter(monthlySale, 2) : 0}`}
          footer={translate("targetReach")}
          meter={targetPct || 1}
          loading={monthlyTargetLoading}
        />
      </Box>

      <SectionHeader
        eyebrow={translate("salesOverview")}
        title={translate("unitsCollectionsTargets")}
        aside={`${tab2 || "ALL"} · ${translate("currentSelection")}`}
      />
      <Box component="section" sx={mdGridSx(MD_GRID.row4)}>
        <UnitSalesCard
          translate={translate}
          loading={unitLoading}
          total={maintRequestTotal}
          parts={parts}
          services={services}
          packages={packages}
          partsPct={partsPct}
          servicesPct={servicesPct}
          packagesPct={packagesPct}
          periodValue={tab2}
          onPeriodChange={(opt) => onTabChange("tab2", opt)}
        />
        <CollectionsBoxesCard
          translate={translate}
          loading={collectionsLoading}
          data={collectionsData}
          periodValue={tab3}
          onPeriodChange={(opt) => onTabChange("tab3", opt)}
          loginUserCurrency={loginUserCurrency}
        />
        <TargetRingCard
          eyebrow={translate("salesTargetMonthly") || "Monthly Sales Target"}
          targetAmount={allSaleData?.monthlyTarget}
          saleAmount={allSaleData?.monthlySale}
          loading={monthlyTargetLoading}
          loginUserCurrency={loginUserCurrency}
          translate={translate}
        />
        <TargetRingCard
          eyebrow={translate("salesTargetYearly") || "Yearly Sales Target"}
          targetAmount={allSaleData?.yearlyTarget}
          saleAmount={allSaleData?.yearlySale}
          loading={yearlyTargetLoading}
          loginUserCurrency={loginUserCurrency}
          translate={translate}
        />
      </Box>

      <SectionHeader
        eyebrow={translate("clients") || "Clients"}
        title={translate("whoBuysWhoOwes")}
      />
      <Box component="section" sx={mdGridSx(MD_GRID.row4)}>
        <MdRankListCard
          eyebrow={
            <>
              {translate("clientSales") || "Top Client by Sales"}
              <MdCurrencyHint currency={loginUserCurrency} />
            </>
          }
          title={translate("top8Clients")}
          loading={salesClientsLoading}
          rows={salesRows}
          money
          currency={loginUserCurrency}
          periodValue={tab5}
          onPeriodChange={(opt) => onTabChange("tab5", opt)}
          periodOptions={FiltersOptionWithoutMTD}
        />
        <FleetSizeCard
          translate={translate}
          loading={fleetLoading}
          totalFleetSize={totalFleetSize}
          rows={fleetRankRows}
          openGraph={openGraph}
          chartOptions={fleetChartOptions}
          onRowClick={onFleetRowClick}
          onBack={onFleetBack}
        />
        <MdRankListCard
          eyebrow={
            <>
              {translate("overduePerClient") || "Overdue Per Client"}
              <MdCurrencyHint currency={loginUserCurrency} />
            </>
          }
          title={translate("highestOverdue")}
          loading={overdueClientsLoading}
          rows={overdueRows}
          money
          currency={loginUserCurrency}
          periodValue={tab7}
          onPeriodChange={(opt) => onTabChange("tab7", opt)}
          periodOptions={FiltersOption}
        />
        <MdRankListCard
          eyebrow={translate("clientMaint.InvoiceCount") || "Job Card Per Client"}
          title={translate("mostJobCards")}
          loading={jobCardsLoading}
          rows={jobRows}
          periodValue={tab8}
          onPeriodChange={(opt) => onTabChange("tab8", opt)}
          periodOptions={FiltersOption}
        />
      </Box>

      <SectionHeader
        eyebrow={translate("topSellers")}
        title={translate("partsServicesPackagesTitle")}
      />
      <Box component="section" sx={mdGridSx(MD_GRID.three)}>
        <MdRankListCard
          eyebrow={translate("topParts") || "Top Parts"}
          title={translate("byUnitsSold")}
          loading={partsLoading}
          rows={partRows}
          periodValue={tab9}
          onPeriodChange={(opt) => onTabChange("tab9", opt)}
          periodOptions={FiltersOption}
        />
        <MdRankListCard
          eyebrow={translate("topServices") || "Top Services"}
          title={translate("byUnitsSold")}
          loading={servicesLoading}
          rows={serviceRows}
          periodValue={tab10}
          onPeriodChange={(opt) => onTabChange("tab10", opt)}
          periodOptions={FiltersOption}
        />
        <MdRankListCard
          eyebrow={translate("topPackages") || "Top Packages"}
          title={translate("byUnitsSold")}
          loading={packagesLoading}
          rows={packageRows}
          periodValue={tab11}
          onPeriodChange={(opt) => onTabChange("tab11", opt)}
          periodOptions={FiltersOption}
        />
      </Box>

      <SectionHeader
        eyebrow={translate("fleetAndProfit")}
        title={translate("vehicleMixGrossProfit")}
      />
      <Box component="section" sx={mdGridSx(MD_GRID.three)}>
        <MdRankListCard
          eyebrow={translate("vehiclesCategoriesCount") || "Vehicles Category Count"}
          title={translate("allRegisteredVehicles")}
          loading={vehicleCatsLoading}
          rows={catRows}
          periodValue={tab13}
          onPeriodChange={(opt) => onTabChange("tab13", opt)}
          periodOptions={FiltersOption}
        />
        <MdRankListCard
          eyebrow={
            <>
              {translate("profitsSAR") || "NTSC Gross Profit"}
              <MdCurrencyHint currency={loginUserCurrency} />
            </>
          }
          title={translate("byClient")}
          loading={profitLoading}
          rows={profitList}
          money
          currency={loginUserCurrency}
          periodValue={tab14}
          onPeriodChange={(opt) => onTabChange("tab14", opt)}
          periodOptions={FiltersOption}
        />
        <MdRankListCard
          eyebrow={translate("stations") || "Job Card Total Per Vertical"}
          title={translate("jobCardsByBusinessLine")}
          loading={stationsLoading}
          rows={stationRows}
          periodValue={tab15}
          onPeriodChange={(opt) => onTabChange("tab15", opt)}
          periodOptions={FiltersOption}
          periodFallback="MTD"
        />
      </Box>

      <SectionHeader
        eyebrow={translate("trendAndVehicles")}
        title={translate("monthlyNetAndCostliest")}
      />
      <Box component="section" sx={mdGridSx(MD_GRID.wide)}>
        <MonthlyNetChartCard
          translate={translate}
          loading={monthlyNetLoading}
          data={monthlySaleNetAmount}
          loginUserCurrency={loginUserCurrency}
        />
        <TopVehiclesCard
          translate={translate}
          loading={vehiclesLoading}
          topVehicles={topVehicles}
          topPerDay={topPerDay}
          topPerKm={topPerKm}
          loginUserCurrency={loginUserCurrency}
        />
      </Box>
    </Box>
  );
};

export default MaintenanceDashboardView;
